import { FC } from 'react';
import type { CourseHearingDerivedStatus } from '@teachur/http-client';
import { Badge, BadgeVariant } from '@new/common/ui/Badge/Badge';

// The admin table shows the hearing's server-computed derivedStatus — the real
// outcome — rather than a raw call status. `approved`/`notYet` are the graded
// results; `cancelled` is a call that ended without a grade; `pending`/`accepted`
// are in-flight. `rejected` is kept as a defensive fallback for any raw status.
// `missed` is a UI/filter overlay (not in derivedStatus): the row's accepted or
// pending attempt has elapsed with no video call. The table passes it in
// explicitly when it detects the condition — see isCallMissed.
type HearingBadgeStatus = CourseHearingDerivedStatus | 'rejected' | 'missed';

const STATUS_VARIANTS: Record<HearingBadgeStatus, BadgeVariant> = {
  pending: 'yellow',
  accepted: 'blue',
  approved: 'green',
  notYet: 'red',
  cancelled: 'default',
  rejected: 'red',
  missed: 'red',
};

const STATUS_LABELS: Record<HearingBadgeStatus, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  approved: 'Approved',
  notYet: 'Not Yet',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
  missed: 'Missed',
};

interface HearingStatusBadgeProps {
  status: HearingBadgeStatus;
}

export const HearingStatusBadge: FC<HearingStatusBadgeProps> = ({ status }) => (
  <Badge variant={STATUS_VARIANTS[status] ?? 'default'}>{STATUS_LABELS[status] ?? status}</Badge>
);
